import type { GridTask, GridTaskTemplate, TemplateSortMode } from "../types";

/**
 * Apply the user's chosen template sort to the visible templates array.
 *
 *  - starts_asc / starts_desc  → by template start trigger offset; due as tiebreaker
 *  - due_asc   / due_desc      → by template due trigger offset; start as tiebreaker
 *  - assignee                  → group by assignee role label (sourced from tasks)
 *  - custom                    → use the manual template_order passed in
 *
 * Pure / testable. Start/due sort keys are integers the backend computes from
 * each template's trigger config (Services::TemplateSorter) — they are
 * journey-independent, so the row order matches what the descriptor reads
 * ("10 days before start" sorts before "7 days before start"). Templates
 * without a sort value fall to the end of either direction.
 *
 * Assignee mode still scans tasks because the role label lives on the task,
 * not the template.
 */
export function sortTemplates(
  templates: GridTaskTemplate[],
  tasks: GridTask[],
  sortKey: TemplateSortMode,
  customOrder: GridTaskTemplate[],
): GridTaskTemplate[] {
  if (sortKey === "custom") return customOrder;

  const sorted = [...templates];

  switch (sortKey) {
    case "starts_asc":
      sorted.sort((a, b) => compareWithFallback(
        a.startAtSortValue, b.startAtSortValue,
        a.dueAtSortValue, b.dueAtSortValue,
      ));
      break;
    case "starts_desc":
      sorted.sort((a, b) => compareWithFallback(
        b.startAtSortValue, a.startAtSortValue,
        b.dueAtSortValue, a.dueAtSortValue,
      ));
      break;
    case "due_asc":
      sorted.sort((a, b) => compareWithFallback(
        a.dueAtSortValue, b.dueAtSortValue,
        a.startAtSortValue, b.startAtSortValue,
      ));
      break;
    case "due_desc":
      sorted.sort((a, b) => compareWithFallback(
        b.dueAtSortValue, a.dueAtSortValue,
        b.startAtSortValue, a.startAtSortValue,
      ));
      break;
    case "assignee": {
      const roleByTemplate = new Map<string, string>();
      tasks.forEach((task) => {
        const key = String(task.templateId);
        if (task.assigneeRole && !roleByTemplate.has(key)) {
          roleByTemplate.set(key, task.assigneeRole);
        }
      });
      sorted.sort((a, b) => {
        const aRole = roleByTemplate.get(String(a.id)) ?? "";
        const bRole = roleByTemplate.get(String(b.id)) ?? "";
        if (aRole === bRole) return a.name.localeCompare(b.name);
        if (!aRole) return 1;
        if (!bRole) return -1;
        return aRole.localeCompare(bRole);
      });
      break;
    }
    case "custom":
    default:
      break;
  }

  return sorted;
}

// Two-key compare: primary first; if equal (or both missing) fall through to
// the secondary key. Missing values always sort to the end of either key.
function compareWithFallback(
  aPrimary: number | null | undefined, bPrimary: number | null | undefined,
  aSecondary: number | null | undefined, bSecondary: number | null | undefined,
): number {
  const primary = compareValue(aPrimary, bPrimary);
  if (primary !== 0) return primary;
  return compareValue(aSecondary, bSecondary);
}

function compareValue(a: number | null | undefined, b: number | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
}
